/**
 * Wave 46 · Housy concierge floating panel.  (Wave 48 rename + pixel mark.)
 *
 * Lives at the app root. Always visible at bottom-right on authenticated
 * pages. Click the little house → panel slides up with the chat UI.
 * Esc or backdrop click closes.
 *
 * The chat connects to POST /v1/agent/chat which streams SSE events:
 *   data: { type: 'text', data: '...' }
 *   data: { type: 'tool_call', data: { name, args } }
 *   data: { type: 'tool_result', data: { name, result | error, refused? } }
 *   data: { type: 'done', data: { conversationId, suspicion } }
 *   data: { type: 'error', data: { message } }
 *
 * We render tool calls as transparent chips ("buscando projetos…") so
 * the user sees the agent doing work, not a spinner.
 */

import { motion, AnimatePresence, useMotionValue, useSpring } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { HouseMark, type HouseMarkState } from './HouseMark';
import { HousyAvatar, type HousyState } from './HousyAvatar';
import { HousyBubble } from './HousyBubble';
import { useAuth } from '@/store/auth.store';
import { useUi } from '@/store/ui.store';
import { api, API_BASE, request } from '@/api/client';
import { useDigest } from '@/hooks/useDigest';
import { cn } from '@/lib/cn';
import { humanizeHttpStatus } from '@/lib/error-humanize';
import { useT } from '@/hooks/useT';

/**
 * Wave 379C · HouseMarkState → HousyState mapping.
 *
 * HouseAgent's internal state alphabet has 9 entries (legacy
 * Wave 379B). HousyAvatar (the new Floating Concierge) carries 6.
 * The 3 extra HouseMark states collapse cleanly:
 *   - hover     → 'idle' (engagement is communicated via eye-track)
 *   - active    → 'idle' (panel-open doesn't need a separate avatar
 *                 state; the panel itself signals activity)
 *   - attention → 'idle' (the unread badge count handles this cue)
 */
function mapToHousyState(state: HouseMarkState): HousyState {
  switch (state) {
    case 'idle':
    case 'hover':
    case 'active':
    case 'attention':
      return 'idle';
    case 'thinking':
    case 'listening':
    case 'speaking':
    case 'error':
    case 'sleeping':
      return state;
    default:
      return 'idle';
  }
}

/**
 * Wave 379B · Sleep state hook.
 *
 * Housy enters 'sleeping' after `delayMs` of no global pointer / touch /
 * keydown activity AND no panel-driven engagement (panel closed,
 * nothing pending, not thinking, not hovered). Wakes on any pointer/
 * touch/key event. Pauses the timer when the document is hidden so we
 * don't drift in and out of sleep with the tab in the background.
 *
 * Implementation note: a single global pointer listener is reused
 * across the lifetime of the hook. We avoid throttling — the listener
 * only writes to a ref and resets a timeout, no React state updates
 * per movement. State (`sleeping` boolean) flips at most twice per
 * inactivity cycle.
 */
function useSleepState({
  open,
  pendingTotal,
  thinking,
  hover,
  delayMs,
  graceUntil,
}: {
  open: boolean;
  pendingTotal: number;
  thinking: boolean;
  hover: boolean;
  delayMs: number;
  graceUntil: number;
}): boolean {
  const [sleeping, setSleeping] = useState(false);

  useEffect(() => {
    // Active interaction signals override sleep — wake immediately and
    // suspend the timer until those signals clear.
    if (open || pendingTotal > 0 || thinking || hover) {
      setSleeping(false);
      return;
    }
    let timer: number | undefined;
    const wake = () => {
      // Don't wake during grace period after manual dismiss/dragoff
      if (graceUntil > Date.now()) {
        return;
      }
      if (sleeping) setSleeping(false);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (!document.hidden) setSleeping(true);
      }, delayMs);
    };
    const onVisibility = () => {
      if (document.hidden) {
        window.clearTimeout(timer);
      } else {
        wake();
      }
    };
    window.addEventListener('pointermove', wake, { passive: true });
    window.addEventListener('pointerdown', wake, { passive: true });
    window.addEventListener('touchstart', wake, { passive: true });
    window.addEventListener('keydown', wake);
    document.addEventListener('visibilitychange', onVisibility);
    wake();
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('pointermove', wake);
      window.removeEventListener('pointerdown', wake);
      window.removeEventListener('touchstart', wake);
      window.removeEventListener('keydown', wake);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [open, pendingTotal, thinking, hover, delayMs, sleeping]);

  return sleeping;
}

/**
 * Where Housy sits on screen. Persisted per-user in localStorage so
 * once you drag him to the top-left of the screen, he stays there.
 * Coordinates are stored as an offset from the DEFAULT bottom-right
 * anchor (positive x = further right, positive y = further down).
 * Default (0, 0) means: bottom-right, 24px from edges.
 */
interface HousyPos {
  x: number;
  y: number;
}
const HOUSY_POS_KEY = 'builder.housy.pos';
function readHousyPos(): HousyPos {
  try {
    const raw = localStorage.getItem(HOUSY_POS_KEY);
    if (!raw) return { x: 0, y: 0 };
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
      return parsed as HousyPos;
    }
  } catch {
    // ignore
  }
  return { x: 0, y: 0 };
}
function writeHousyPos(p: HousyPos): void {
  try {
    localStorage.setItem(HOUSY_POS_KEY, JSON.stringify(p));
  } catch {
    // ignore (private browsing)
  }
}
function clampHousyPos(p: HousyPos): HousyPos {
  if (typeof window === 'undefined') return p;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const maxLeft = 24 + 64 - (vw - 24);
  const maxRight = 24;
  // L4 fix · 2026-06-03 — reserve a 140px top safe-zone (TopBar 64 +
  // ProjectHero ~61 + margin) so the mascot can never be dragged/parked
  // over the app header controls. Previously maxUp let its top reach y≈16.
  const TOP_SAFE_ZONE = 140;
  const maxUp = 24 + 64 + TOP_SAFE_ZONE - (vh - 24);
  const maxDown = 24;
  return {
    x: Math.max(maxLeft, Math.min(maxRight, p.x)),
    y: Math.max(maxUp, Math.min(maxDown, p.y)),
  };
}

type Role = 'user' | 'assistant';

interface ChatTurn {
  role: Role;
  content: string;
  toolEvents?: Array<{ kind: 'call' | 'result'; name: string; detail?: string }>;
}

const EASE = [0.22, 1, 0.36, 1] as const;

export function HouseAgent() {
  const { locale } = useT();
  const isEn = locale === 'en';
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  // Wave 285b — when JP clicks the × on the "obra atual" chip he's
  // saying "answer me as if I'm not on this obra page". We can't change
  // the URL (he's still on /projects/:id and might want to come back),
  // so we keep a local flag that suppresses the chip AND nulls the
  // projectId we send in `context.projectId`. Reset on each project
  // change. Refresh restores the lock — that's a feature, not a bug.
  const [globalMode, setGlobalMode] = useState(false);
  const [recording, setRecording] = useState(false);
  // Wave 379B · input-focused gates the 'listening' state; aria-live
  // narrates state changes so screen readers feel the character too.
  const [inputFocused, setInputFocused] = useState(false);
  // Wave 343 · inline error banner for voice flow. Replaces three
  // native alert() calls with a 5s-auto-dismissing inline notice.
  const [voiceError, setVoiceError] = useState<string | null>(null);
  // Wave 46-grace · grace period after manual dismiss/dragoff so Housy
  // doesn't auto-reappear on the next pointer move. Duration: 60s.
  const [graceUntil, setGraceUntil] = useState(0);
  useEffect(() => {
    if (!voiceError) return;
    const t = window.setTimeout(() => setVoiceError(null), 5000);
    return () => window.clearTimeout(t);
  }, [voiceError]);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  // Wave 295 — panel ref so we can trap focus inside the dialog and
  // close on Escape (a11y baseline for any modal).
  const panelRef = useRef<HTMLElement>(null);
  // Wave 90 · thread switcher — recent conversations grouped by their
  // last referenced project, so JP can flip between obras without
  // losing context. Loaded lazily when the panel opens.
  const [threadsOpen, setThreadsOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [threads, setThreads] = useState<Array<{
    conversationId: string; lastAt: string; messageCount: number; preview: string | null;
  }> | null>(null);

  const loadThreads = async () => {
    try {
      const rows = await api.listAgentConversations();
      setThreads(rows);
    } catch {
      setThreads([]);
    }
  };
  const openConversation = async (cid: string) => {
    try {
      const history = await api.getAgentConversation(cid);
      const restored: ChatTurn[] = history
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role as Role, content: m.content }));
      setTurns(restored);
      setConversationId(cid);
      setThreadsOpen(false);
    } catch {
      // Best-effort — leave the existing thread in place if load failed.
    }
  };
  const token = useAuth((s) => s.token);
  const location = useLocation();
  const params = useParams();
  const bodyRef = useRef<HTMLDivElement>(null);

  // Wave 95.2 — pending counts that drive Housy's attention
  // animation and fallback bubble. The digest handles 4 of the 5
  // categories; pending user registrations live on a separate route
  // (admin/PM only) so we poll it lightly here. Failures degrade to
  // zero — no blocker for the rest of the UI.
  const digest = useDigest();
  // Wave 285b · re-lock to the URL-bound project whenever the path
  // changes. Without this, JP toggles "geral" once and the next obra
  // he visits stays in geral mode unexpectedly.
  useEffect(() => {
    setGlobalMode(false);
  }, [(params as { id?: string }).id]);

  // Wave 298 — `inert` attribute on background while panel is open.
  // Modern alternative to manual focus trapping: the browser refuses to
  // focus anything inside an inert subtree, so Tab naturally cycles only
  // inside the dialog. Falls back to the explicit Tab trap below for
  // browsers without inert support (Safari < 15.5 / Firefox < 112).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const main = document.querySelector('main');
    const sidenav = document.querySelector('nav[data-sidenav]');
    if (open) {
      main?.setAttribute('inert', '');
      sidenav?.setAttribute('inert', '');
    } else {
      main?.removeAttribute('inert');
      sidenav?.removeAttribute('inert');
    }
    return () => {
      main?.removeAttribute('inert');
      sidenav?.removeAttribute('inert');
    };
  }, [open]);

  // Wave 295 — Esc-to-close + Tab focus trap on the open panel.
  // Standard a11y baseline: any modal that takes focus should also
  // release it on Escape, and Tab should cycle inside while open.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;

    const focusable = panel.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const trap = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', trap);
    // Move focus into panel on open so screen-readers announce the dialog.
    first?.focus();

    return () => document.removeEventListener('keydown', trap);
  }, [open]);

  const [pendingUsers, setPendingUsers] = useState<number>(0);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const pull = async () => {
      if (cancelled || document.hidden) return;
      try {
        const r = await request<Array<unknown>>('/v1/users/pending');
        const list = r;
        if (!cancelled) setPendingUsers(list.length);
      } catch {
        // Non-PM/admin returns 403 — ignore.
      }
    };
    pull();
    const iv = window.setInterval(pull, 90_000);
    return () => {
      cancelled = true;
      window.clearInterval(iv);
    };
  }, [token]);

  const totalPending =
    (digest?.total ?? 0) + (pendingUsers > 0 ? pendingUsers : 0);

  // Wave 75 · push-to-talk voice. Hold the mic button to record, release
  // to transcribe. The transcript lands in `draft` so the user can edit
  // before sending — a Whisper miss is annoying but not destructive.
  // Falls back gracefully if MediaRecorder isn't available (Safari ≤14).
  const startRecording = async () => {
    if (recording) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setVoiceError(isEn ? 'Your browser does not support audio recording.' : 'Seu navegador não suporta gravação de áudio.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      recordChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recordChunksRef.current, { type: 'audio/webm' });
        if (blob.size === 0) return;
        try {
          const res = await fetch(`${API_BASE}/v1/agent/voice`, {
            method: 'POST',
            headers: {
              'content-type': blob.type || 'audio/webm',
              authorization: `Bearer ${token}`,
            },
            body: blob,
          });
          // 2026-05-20 · Human PT-BR copy instead of raw HTTP code so the PM
          // sees "Sua sessão expirou…" instead of "http_401".
          if (!res.ok) throw new Error(humanizeHttpStatus(res.status));
          const json = (await res.json()) as { data?: { text?: string } };
          const text = (json.data?.text ?? '').trim();
          if (text) setDraft((d) => (d ? `${d} ${text}` : text));
        } catch (err) {
          setVoiceError(isEn
            ? `Could not transcribe: ${err instanceof Error ? err.message : 'error'}`
            : `Não consegui transcrever: ${err instanceof Error ? err.message : 'erro'}`);
        }
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
    } catch {
      setVoiceError(isEn ? 'Microphone permission denied.' : 'Permissão de microfone negada.');
    }
  };
  const stopRecording = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      recorderRef.current = null;
      setRecording(false);
    }
  };

  // Auto-scroll to bottom on new content
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [turns, thinking]);

  // Wave 43 · Bug 5 — Ctrl+H / Cmd+H opens/closes Housy.
  // Legacy Cmd+K binding removed to avoid conflict with CommandBar.
  // Ctrl+Shift+H resets position (was Cmd+Shift+K).
  // Also listens to `housy:toggle` CustomEvent dispatched by the TopBar
  // icon button so the button and the shortcut share one code path.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setGraceUntil(Date.now() + 60_000);
        return;
      }
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === 'h') {
        if (e.shiftKey) {
          e.preventDefault();
          window.dispatchEvent(new CustomEvent('housy:reset'));
          return;
        }
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onToggle = () => setOpen((v) => !v);
    window.addEventListener('keydown', onKey);
    window.addEventListener('housy:toggle', onToggle);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('housy:toggle', onToggle);
    };
  }, []);

  // Wave 379AD · bridge do VoiceMicButton. Ouve `housy:send` (CustomEvent
  // com {detail:{text,source,commandId}}), abre o panel e dispara send
  // com o texto da transcrição. Sem acoplamento direto entre voice e
  // housy — a ponte é o evento.
  useEffect(() => {
    const onVoice = (e: Event) => {
      const detail = (e as CustomEvent<{ text?: string; source?: string }>).detail;
      const text = detail?.text?.trim();
      if (!text) return;
      setOpen(true);
      // Aguarda 1 frame pra o panel abrir antes de enviar (evita
      // race com setOpen em alguns navegadores).
      requestAnimationFrame(() => { void send(text); });
    };
    window.addEventListener('housy:send', onVoice as EventListener);
    return () => window.removeEventListener('housy:send', onVoice as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Don't render on unauthenticated pages (login, public share viewers).
  if (!token) return null;

  // Wave 379B · cinematic state derivation. Priority tree (top wins):
  //   1. error      voiceError set (live banner)
  //   2. speaking   thinking && last assistant turn has content
  //   3. listening  panel open && (input focused || recording)
  //   4. thinking   request in flight, no content yet
  //   5. hover      pointer over the floater
  //   6. attention  panel closed AND pendingTotal > 0
  //   7. active     panel open, no other state firing
  //   8. sleeping   inactive ≥ 60s (driven by useSleepState)
  //   9. idle       default
  // The hover/active branches are unchanged from the prior derivation;
  // we just inserted the new states at appropriate priority points.
  const lastAssistant = turns[turns.length - 1];
  const isSpeaking = thinking && (lastAssistant?.content?.length ?? 0) > 0;
  const isListening = open && (inputFocused || recording);
  const sleeping = useSleepState({
    open,
    pendingTotal: totalPending,
    thinking,
    hover,
    delayMs: 60_000,
    graceUntil,
  });
  const state: HouseMarkState = voiceError
    ? 'error'
    : isSpeaking
      ? 'speaking'
      : isListening
        ? 'listening'
        : thinking
          ? 'thinking'
          : hover
            ? 'hover'
            : !open && totalPending > 0
              ? 'attention'
              : open
                ? 'active'
                : sleeping
                  ? 'sleeping'
                  : 'idle';

  async function send(injectedText?: string) {
    const text = (injectedText ?? draft).trim();
    if (!text || thinking) return;
    if (!injectedText) setDraft('');
    const nextTurns: ChatTurn[] = [
      ...turns,
      { role: 'user', content: text },
      { role: 'assistant', content: '', toolEvents: [] },
    ];
    setTurns(nextTurns);
    setThinking(true);

    try {
      const res = await fetch(`${API_BASE}/v1/agent/chat`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: nextTurns
            .filter((t) => t.content || t.role === 'assistant')
            .slice(0, -1) // drop the empty assistant placeholder we added above
            .map((t) => ({ role: t.role, content: t.content })),
          context: {
            pageRoute: location.pathname,
            projectId: globalMode ? null : (params as { id?: string }).id ?? null,
          },
          conversationId,
        }),
      });
      if (!res.ok || !res.body) {
        // 2026-05-20 · Render PT-BR copy in the chat bubble (the catch below
        // falls back to err.message) instead of leaking "http_500".
        throw new Error(humanizeHttpStatus(res.status));
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buffer.indexOf('\n\n')) !== -1) {
          const chunk = buffer.slice(0, nl);
          buffer = buffer.slice(nl + 2);
          if (!chunk.startsWith('data: ')) continue;
          try {
            const ev = JSON.parse(chunk.slice(6));
            setTurns((prev) => {
              const out = [...prev];
              const last = out[out.length - 1];
              if (!last || last.role !== 'assistant') return prev;
              if (ev.type === 'text') {
                last.content += ev.data;
              } else if (ev.type === 'tool_call') {
                last.toolEvents = [
                  ...(last.toolEvents ?? []),
                  { kind: 'call', name: ev.data.name },
                ];
              } else if (ev.type === 'tool_result') {
                last.toolEvents = [
                  ...(last.toolEvents ?? []),
                  {
                    kind: 'result',
                    name: ev.data.name,
                    detail: ev.data.refused
                      ? 'acesso negado'
                      : ev.data.error
                        ? 'erro'
                        : 'ok',
                  },
                ];
              } else if (ev.type === 'done') {
                setConversationId(ev.data.conversationId);
              } else if (ev.type === 'error') {
                // Wave 379P · The server-emitted error frame used to be parsed
                // and silently dropped — UI showed the "thinking" spinner end
                // with no message, so the user thought Housy was online but
                // ignoring them. Now we render the error inline as the
                // assistant's reply so the failure mode is visible.
                const detail = typeof ev.data === 'object' && ev.data !== null
                  ? (ev.data.message ?? ev.data.code ?? JSON.stringify(ev.data))
                  : String(ev.data ?? (isEn ? 'unknown error' : 'erro desconhecido'));
                last.content = last.content
                  ? `${last.content}\n\n⚠ ${detail}`
                  : isEn
                    ? `⚠ Could not respond right now: ${detail}. Try again in 1 min — if it persists, let JP know.`
                    : `⚠ Não consegui responder agora: ${detail}. Tenta de novo em 1 min — se persistir, me chama o JP.`;
              }
              return out;
            });
          } catch {
            // Skip malformed SSE frame
          }
        }
      }
      // Wave 379P · empty-stream guard. If the LLM closed the stream
      // without ever writing text, replace the empty bubble so the user
      // doesn't see "online but silent". Only triggers when there are no
      // tool events either — when there ARE tool events the digest synth
      // in HousyBubble already provides a fallback.
      setTurns((prev) => {
        const out = [...prev];
        const last = out[out.length - 1];
        if (last && last.role === 'assistant' && !last.content && (!last.toolEvents || last.toolEvents.length === 0)) {
          last.content = isEn
            ? "I'm still learning how to answer this. Try asking in a different way or describe what you need to do."
            : 'Ainda estou aprendendo a responder isso. Tenta perguntar de outro jeito ou descreve o que você precisa fazer.';
        }
        return out;
      });
    } catch (err) {
      setTurns((prev) => {
        const out = [...prev];
        const last = out[out.length - 1];
        if (last && last.role === 'assistant') {
          last.content = last.content || (isEn
            ? `Something went wrong: ${err instanceof Error ? err.message : String(err)}`
            : `Deu ruim: ${err instanceof Error ? err.message : String(err)}`);
        }
        return out;
      });
    } finally {
      setThinking(false);
    }
  }

  // Wave 320 — hide the floating mark + the proactive bubble while
  // Prumo is open. Otherwise the casinha sits over Prumo's header (its
  // z-[60] ties Prumo's old backdrop, and DOM order let HouseAgent win).
  // Prumo is now z-[100/110] but we still hide here to avoid a stacked
  // FAB cluttering the dialog the user is engaging with.
  const prumoOpen = useUi((s) => s.prumoOpen);

  return (
    <>
      {!prumoOpen && (
        // Wave 380 · FIX 2 — bubble is now rendered as an absolute
        // overlay INSIDE HousyFloater so it inherits the same drag x/y
        // transform as the avatar. Eliminates the localStorage poll
        // (Wave 332 hack) and "balão longe do Housy" desync entirely.
        <HousyFloater
          state={state}
          pendingTotal={totalPending}
          setHover={setHover}
          onOpen={() => setOpen(true)}
          chatOpen={open}
          digest={digest}
          pendingUsers={pendingUsers}
        />
      )}

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-[70] bg-ink/30 backdrop-blur-[2px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22, ease: EASE }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              ref={panelRef}
              role="dialog"
              aria-modal
              className={cn(
                'fixed z-[80] flex flex-col overflow-hidden border border-line bg-surface shadow-elev-3',
                'bottom-0 right-0 w-full h-[82vh] rounded-t-2xl',
                'sm:bottom-6 sm:right-6 sm:h-[560px] sm:w-[420px] sm:rounded-2xl',
              )}
              // Wave 298 — Emil's compositor rule: Framer Motion `y/scale`
              // shorthand uses requestAnimationFrame on the main thread; full
              // transform strings stay on the GPU compositor and don't drop
              // frames during TanStack Query refetches.
              initial={{ opacity: 0, transform: 'translateY(24px) scale(0.98)' }}
              animate={{ opacity: 1, transform: 'translateY(0px) scale(1)' }}
              exit={{ opacity: 0, transform: 'translateY(24px) scale(0.98)' }}
              transition={{ duration: 0.26, ease: EASE }}
            >
              <header className="flex items-center justify-between border-b border-line px-4 py-3">
                <div className="flex items-center gap-2">
                  {/* Wave 379C — chat panel header uses the new
                      Floating Concierge avatar at 28px. The Avatar
                      scales gracefully via its 100×100 viewBox. */}
                  <HousyAvatar
                    state={mapToHousyState(thinking ? 'thinking' : 'idle')}
                    size={28}
                    mode="auto"
                    sleepAfterMs={0}
                  />
                  <span className="text-[13px] font-medium text-ink">Housy</span>
                  {/* Wave 74 / Wave 285b · per-project chip.
                      - When chat opens on /projects/:id, Housy is locked
                        to that obra. The chip says "obra" — clicking ×
                        flips into "geral" mode (project context nulled
                        out, see globalMode in `context.projectId`).
                      - When in geral mode, we show a faded "geral" chip
                        with a back-arrow that re-locks to the obra.
                      - Refresh always re-locks (state is local). */}
                  {(params as { id?: string }).id && !globalMode && (
                    <button
                      type="button"
                      onClick={() => {
                        setGlobalMode(true);
                        setConversationId(null);
                        setTurns([]);
                      }}
                      className="ml-1 inline-flex items-center gap-1 rounded-full border border-line/70 bg-transparent px-1.5 py-px text-[10px] text-ink-subtle hover:border-line hover:text-ink"
                      title={isEn ? 'Leave project context (global mode)' : 'Sair do contexto da obra (modo geral)'}
                    >
                      <span>{isEn ? 'project' : 'obra'}</span>
                      <span aria-hidden className="text-[11px] leading-none">×</span>
                    </button>
                  )}
                  {(params as { id?: string }).id && globalMode && (
                    <button
                      type="button"
                      onClick={() => {
                        setGlobalMode(false);
                        setConversationId(null);
                        setTurns([]);
                      }}
                      className="ml-1 inline-flex items-center gap-1 rounded-full border border-line/70 bg-transparent px-1.5 py-px text-[10px] text-ink-subtle hover:border-line hover:text-ink"
                      title={isEn ? 'Back to project context' : 'Voltar para o contexto da obra'}
                    >
                      <span aria-hidden>↩</span>
                      <span>{isEn ? 'global' : 'geral'}</span>
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      setThreadsOpen((v) => {
                        const next = !v;
                        if (next && threads === null) void loadThreads();
                        return next;
                      });
                    }}
                    className="pressable rounded-lg px-2 py-1 text-[11px] uppercase tracking-[0.18em] text-ink-muted hover:text-ink"
                    aria-label={isEn ? 'Conversations' : 'Conversas'}
                    title={isEn ? 'Previous conversations' : 'Conversas anteriores'}
                  >
                    {threadsOpen ? (isEn ? 'Close' : 'Fechar') : (isEn ? 'Conversations' : 'Conversas')}
                  </button>
                  {/* Wave 314L — canal de feedback. Vai pra curadoria
                      (JP+Claude) antes de virar backlog. */}
                  <button
                    type="button"
                    onClick={() => setFeedbackOpen(true)}
                    className="pressable rounded-lg px-2 py-1 text-[11px] uppercase tracking-[0.18em] text-ink-muted hover:text-ink"
                    aria-label={isEn ? 'Send feedback' : 'Enviar feedback'}
                    title={isEn ? 'Send feedback' : 'Enviar feedback (vai pra curadoria antes de virar backlog)'}
                  >
                    Feedback
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setConversationId(null);
                      setTurns([]);
                    }}
                    className="pressable rounded-lg px-2 py-1 text-[11px] uppercase tracking-[0.18em] text-ink-muted hover:text-ink"
                    aria-label={isEn ? 'New conversation' : 'Nova conversa'}
                    title={isEn ? 'New conversation' : 'Nova conversa'}
                  >
                    {isEn ? 'New' : 'Nova'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="pressable rounded-lg px-2 py-1 text-sm text-ink-muted hover:text-ink"
                    aria-label={isEn ? 'Close' : 'Fechar'}
                  >
                    ✕
                  </button>
                </div>
              </header>

              {feedbackOpen && (
                <FeedbackPanel
                  routeContext={location.pathname}
                  onClose={() => setFeedbackOpen(false)}
                />
              )}

              {threadsOpen && (
                <div className="max-h-72 overflow-y-auto border-b border-line bg-surface-2/40 px-3 py-2 text-sm">
                  <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
                    {isEn ? 'Previous conversations' : 'Conversas anteriores'}
                  </p>
                  {threads === null ? (
                    <p className="text-ink-subtle">{isEn ? 'Loading…' : 'Carregando…'}</p>
                  ) : threads.length === 0 ? (
                    <p className="text-ink-subtle">{isEn ? 'No previous conversations.' : 'Nenhuma conversa anterior.'}</p>
                  ) : (
                    <ul className="space-y-1">
                      {threads.map((t) => (
                        <li key={t.conversationId}>
                          <button
                            type="button"
                            onClick={() => void openConversation(t.conversationId)}
                            className={cn(
                              'block w-full rounded px-2 py-1.5 text-left text-[12px] hover:bg-surface',
                              t.conversationId === conversationId && 'bg-surface',
                            )}
                          >
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="truncate text-ink">
                                {t.preview ?? (isEn ? '(no messages)' : '(sem mensagens)')}
                              </span>
                              <span className="shrink-0 text-[10px] text-ink-subtle">
                                {new Date(t.lastAt).toLocaleDateString(isEn ? 'en-US' : 'pt-BR')}
                              </span>
                            </div>
                            <span className="text-[10px] text-ink-subtle">
                              {t.messageCount} {isEn ? 'messages' : 'mensagens'}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div ref={bodyRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4 text-sm">
                {turns.length === 0 && (
                  <EmptyState
                    onPick={(q) => setDraft(q)}
                    route={location.pathname}
                    isEn={isEn}
                  />
                )}
                {turns.map((t, i) => (
                  <TurnView key={i} turn={t} />
                ))}
                {thinking && (
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink-subtle">
                    <span className="flex items-center gap-1" aria-label={isEn ? 'Housy is thinking' : 'Housy está pensando'}>
                      {[0, 1, 2].map((i) => (
                        <span
                          key={i}
                          className="h-2 w-2 rounded-full bg-brand-400"
                          style={{
                            animation: 'dot-pulse 1.2s ease-in-out infinite',
                            animationDelay: `${i * 200}ms`,
                          }}
                          aria-hidden
                        />
                      ))}
                    </span>
                    {isEn ? 'Thinking' : 'Pensando'}
                  </div>
                )}
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void send();
                }}
                className="flex items-end gap-2 border-t border-line px-3 py-3"
              >
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setInputFocused(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  placeholder={isEn ? 'Ask anything…  (Shift+Enter for new line)' : 'Pergunta aí…  (Shift+Enter pra quebrar linha)'}
                  rows={1}
                  className="flex-1 resize-none rounded-xl border border-line bg-surface-2 px-3 py-2 text-[14px] text-ink placeholder:text-ink-subtle focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/25"
                  style={{ minHeight: 40, maxHeight: 140 }}
                />
                <button
                  type="button"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    void startRecording();
                  }}
                  onPointerUp={(e) => {
                    e.preventDefault();
                    stopRecording();
                  }}
                  onPointerLeave={() => {
                    if (recording) stopRecording();
                  }}
                  aria-label={recording ? (isEn ? 'Release to send' : 'Soltar para enviar') : (isEn ? 'Hold to speak' : 'Segurar para falar')}
                  title={recording ? (isEn ? 'Release to transcribe' : 'Soltar para transcrever') : (isEn ? 'Hold to speak' : 'Segurar para falar')}
                  className={cn(
                    'pressable inline-flex h-10 w-10 items-center justify-center rounded-xl border border-line text-base',
                    recording
                      ? 'border-red-400 bg-red-500/10 text-red-300'
                      : 'bg-surface-2 text-ink-muted hover:text-ink',
                  )}
                >
                  <span aria-hidden>{recording ? '●' : '🎤'}</span>
                </button>
                <button
                  type="submit"
                  disabled={!draft.trim() || thinking}
                  className="pressable inline-flex h-10 items-center rounded-xl bg-ink px-4 text-sm font-medium text-surface hover:bg-ink/95 disabled:opacity-50"
                >
                  {isEn ? 'Send' : 'Enviar'}
                </button>
              </form>
              {/* Wave 343 · inline voice error replaces three native
                  alert() popups. Auto-dismisses after 5s. */}
              {voiceError && (
                <div
                  role="status"
                  className="mx-3 mb-2 rounded-md border border-err/40 bg-err/10 px-3 py-2 text-[12px] text-err"
                >
                  {voiceError}
                </div>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/**
 * Wave 49 · Housy's physical presence.
 *
 * No border, no chrome — just the pixel house floating with a soft
 * drop-shadow. Constant idle bob (sub-perceptual vertical breathing)
 * so he reads as alive without being distracting. Drag to move him
 * anywhere: spring-powered handoff (stiffness 280, damping 22) means
 * release feels weighty, not twitchy. Position persists per browser.
 *
 * Drag vs click disambiguation: framer-motion's drag fires `onDrag`
 * whenever the pointer moves past `dragPropagation` threshold. We only
 * treat pointer-up as a click if the total travel during the gesture
 * stayed under 4 px.
 */
function HousyFloater({
  state,
  pendingTotal,
  setHover,
  onOpen,
  chatOpen,
  digest,
  pendingUsers,
}: {
  state: HouseMarkState;
  pendingTotal: number;
  setHover: (h: boolean) => void;
  onOpen: () => void;
  // Wave 380 · FIX 2 — bubble props passed through so the proactive
  // bubble lives inside the same motion container as the avatar and
  // inherits the live drag offset (no more localStorage polling).
  chatOpen: boolean;
  digest: ReturnType<typeof useDigest>;
  pendingUsers: number;
}) {
  const initialPos = useRef<HousyPos>(readHousyPos());
  // Convert stored offset to motion values. Bottom-right origin means
  // negative X moves left of anchor, negative Y moves up.
  const x = useMotionValue(initialPos.current.x);
  const y = useMotionValue(initialPos.current.y);
  // Spring-smoothed body so non-drag motion (idle bob, hover lift) has
  // weight without fighting the drag translate.
  const bobY = useSpring(0, { stiffness: 100, damping: 16, mass: 0.8 });
  // Wave 379B · drag-velocity tilt. While dragging, Housy rotates a
  // little in the direction of velocity; on release the spring pulls
  // it back to 0 with a slight elastic overshoot. Capped at ±10°.
  const tilt = useSpring(0, { stiffness: 240, damping: 18, mass: 0.6 });
  // CP-005 · drag velocity passed through to HousyAvatar so it can
  // drive body squash/stretch + halo deformation + roof lag + badge
  // lag at the avatar level. Raw px/s; HousyAvatar clamps internally.
  // Snapped to 0 on drag end so the lag layers settle through their
  // own springs instead of staying at fling-velocity forever.
  const velocityX = useMotionValue(0);
  const velocityY = useMotionValue(0);
  const [dragging, setDragging] = useState(false);
  // True for the duration of a real drag (>3px). We use this to suppress
  // the synthetic onTap that fires after onDragEnd so a drag doesn't
  // also open the panel.
  const movedDuringDrag = useRef(false);

  // Cmd-Shift-K → snap Housy back to the bottom-right default.
  useEffect(() => {
    const onReset = () => {
      x.set(0);
      y.set(0);
      writeHousyPos({ x: 0, y: 0 });
    };
    window.addEventListener('housy:reset', onReset);
    return () => window.removeEventListener('housy:reset', onReset);
  }, [x, y]);

  // Clamp persisted desktop coordinates as soon as Housy mounts on a
  // smaller viewport, and again on resize/orientation changes.
  useEffect(() => {
    const applyViewportClamp = () => {
      const current = { x: x.get(), y: y.get() };
      const next = clampHousyPos(current);
      if (next.x === current.x && next.y === current.y) return;
      x.set(next.x);
      y.set(next.y);
      writeHousyPos(next);
    };
    applyViewportClamp();
    window.addEventListener('resize', applyViewportClamp);
    window.addEventListener('orientationchange', applyViewportClamp);
    return () => {
      window.removeEventListener('resize', applyViewportClamp);
      window.removeEventListener('orientationchange', applyViewportClamp);
    };
  }, [x, y]);

  // Idle bob loop — drives bobY between 0 and -4 over 2.6s. Matches
  // the breathing cadence of the mark so Housy reads as a single
  // creature, not a logo + animation.
  // Wave 95.2 — when Housy is in 'attention' state we double the
  // amplitude AND speed up the cadence so the motion actually reads
  // as motion (JP's feedback: the 4px bob never landed for him).
  useEffect(() => {
    let alive = true;
    let up = true;
    const ampHigh = state === 'attention' ? -10 : -4;
    const period = state === 'attention' ? 1100 : 2600;
    const step = () => {
      if (!alive) return;
      bobY.set(up ? ampHigh : 0);
      up = !up;
    };
    step();
    const id = window.setInterval(step, period);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [bobY, state]);

  // Persist position after each drag.
  const savePos = () => {
    writeHousyPos({ x: x.get(), y: y.get() });
  };

  // Wave 379B · aria-live narration. Maps each Housy state to a short
  // pt-BR phrase a screen reader can announce. We debounce announcements
  // so a fast state flip (e.g. listening → speaking) doesn't queue two
  // utterances inside one second.
  const [announce, setAnnounce] = useState<string>('');
  const lastAnnounceAt = useRef(0);
  useEffect(() => {
    // Note: aria-live phrases are always in PT-BR — screen readers on
    // PT-BR devices announce these; the locale flag is not available
    // here (HousyFloater has no hook access to useT). Kept as PT-BR
    // since these are screen-reader only and the product is PT-BR first.
    const phrases: Record<HouseMarkState, string> = {
      idle: '',
      hover: '',
      active: 'Housy aberto',
      thinking: 'Housy pensando',
      attention: 'Housy tem novidades',
      listening: 'Housy ouvindo',
      speaking: 'Housy respondendo',
      error: 'Housy reportou um erro',
      sleeping: '',
    };
    const phrase = phrases[state] || '';
    if (!phrase) return;
    const now = Date.now();
    if (now - lastAnnounceAt.current < 900) return;
    lastAnnounceAt.current = now;
    setAnnounce(phrase);
    const t = window.setTimeout(() => setAnnounce(''), 1500);
    return () => window.clearTimeout(t);
  }, [state]);

  return (
    <motion.div
      // Anchored to bottom-right. drag lifts it onto the pointer; release
      // snaps via spring to where it was left.
      // L4 fix · 2026-06-03 — was z-[60], which sat ABOVE the TopBar (z-30)
      // and ProjectHero (z-20), so the dragged mascot covered the header
      // "?"/"⋯" controls and bell/avatar. z-[15] keeps it above page content
      // but BELOW the app chrome headers, so it ducks behind them instead of
      // obstructing controls. (Drag clamp below also keeps it out of the top.)
      className="fixed bottom-6 right-6 z-[15] select-none"
      style={{ x, y, touchAction: 'none' }}
      drag
      dragMomentum={true}
      dragElastic={0.18}
      dragTransition={{ bounceStiffness: 280, bounceDamping: 22, power: 0.3, timeConstant: 200 }}
      onDragStart={() => {
        setDragging(true);
        movedDuringDrag.current = false;
      }}
      onDrag={(_, info) => {
        // If the user has moved more than 3 px during this gesture,
        // treat the upcoming pointer-up as a drag (not a tap).
        if (Math.abs(info.offset.x) > 3 || Math.abs(info.offset.y) > 3) {
          movedDuringDrag.current = true;
        }
        // Wave 379B · velocity-driven tilt. info.velocity is in px/s;
        // scale + clamp to ±10°. Negative velocity (leftward) tilts
        // Housy left, mirroring physical lean into motion.
        const vx = info.velocity.x;
        const vy = info.velocity.y;
        const t = Math.max(-10, Math.min(10, vx * 0.012));
        tilt.set(t);
        // CP-005 · raw velocity also feeds HousyAvatar's drag-physics
        // springs (squash, halo, roof lag, badge lag).
        velocityX.set(vx);
        velocityY.set(vy);
      }}
      onDragEnd={() => {
        setDragging(false);
        // Wave 379B · spring back to upright on release. The spring
        // config gives a soft overshoot (≈ -0.6° on the rebound) which
        // reads as elastic mass without being theatrical.
        tilt.set(0);
        // CP-005 · let the lag/squash layers in HousyAvatar settle.
        velocityX.set(0);
        velocityY.set(0);
        const next = clampHousyPos({ x: x.get(), y: y.get() });
        x.set(next.x);
        y.set(next.y);
        writeHousyPos(next);
      }}
      // framer-motion's `onTap` fires when the user pressed and released
      // without crossing the drag threshold. Combined with the
      // movedDuringDrag flag this gives us a clean click vs drag split:
      // a static press → onTap fires → opens the panel.
      onTap={() => {
        if (movedDuringDrag.current) {
          movedDuringDrag.current = false;
          return;
        }
        onOpen();
      }}
      whileDrag={{ scale: 1.08 }}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 260, damping: 18 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Wave 380 · FIX 2 — proactive bubble rendered as absolute
          child of the floater container. Inherits the parent's x/y
          drag transform automatically; positions itself above the
          avatar (8 px gap) with a tail pointing down at it. The
          inner bobbing motion.div doesn't wrap the bubble so the
          bubble doesn't bob with the avatar (looks calmer that way;
          drag offset still tracks since drag is on the *outer* div). */}
      <HousyBubble
        chatOpen={chatOpen}
        onOpenChat={onOpen}
        digest={digest}
        pendingUsers={pendingUsers}
      />
      <motion.div
        style={{ y: bobY, rotate: tilt }}
        // Wave 295/296 — guarantee 44×44 minimum hit area on touch devices.
        // The visible HouseMark may render smaller depending on state; the
        // wrapper ensures the tap target stays accessible regardless.
        // Wave 379B · `rotate: tilt` adds drag-velocity lean.
        className="housy-drop-shadow flex min-h-[44px] min-w-[44px] items-center justify-center relative"
      >
        {/* Wave 379C — the legacy attention-glow halo (Wave 95.2) was
            removed because HousyAvatar's own dual halo (cinematic
            wide-ambient + tight under-lamp) is the new attention
            channel. Halo intensity ramps with state inside the avatar. */}
        {/* Wave 379C — replaced HouseMark (16-grid pixel art) with the
            new HousyAvatar (Floating Concierge). The 9-state vocabulary
            collapses into HousyAvatar's 6 states; hover/active/attention
            all read as `idle` because the badge handles the
            "needs-attention" cue and eye-tracking handles the
            engagement cue. Drag tilt is wired through the live `tilt`
            MotionValue so the roof + badge follow-through stays
            frame-synced. */}
        <HousyAvatar
          state={mapToHousyState(dragging ? 'active' : state)}
          unreadCount={pendingTotal}
          size={72}
          mode="auto"
          // Pass no-op onClick because we drive click via drag-end.
          onClick={() => {}}
          externalTiltX={tilt}
          // CP-005 · velocity wire-through for body squash, halo
          // deform, roof lag, badge lag. HousyAvatar clamps internally.
          externalVelocityX={velocityX}
          externalVelocityY={velocityY}
          // We don't want HousyAvatar's *own* sleep timer fighting
          // HouseAgent's useSleepState; pass 0 to disable.
          sleepAfterMs={0}
          className="cursor-grab active:cursor-grabbing"
        />
        {/* Wave 379C — the legacy pending-count chip was removed.
            HousyAvatar now owns the badge as part of the architectural
            character (anchored next to the chimney, with an outer ring
            that reads as part of the building, not pasted on top). */}
      </motion.div>
      {/* Drop shadow: a blurred ellipse beneath Housy that doesn't
          travel with the bob. Pure decoration, fades when he lifts. */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute left-1/2 top-full -translate-x-1/2 rounded-full',
          'bg-ink/25 blur-md dark:bg-black/55',
          'transition-opacity duration-200',
          dragging ? 'opacity-40 scale-90' : 'opacity-70',
        )}
        style={{ width: 44, height: 8, marginTop: -2 }}
      />
      {/* Wave 379B · aria-live region for state narration. Visually
          hidden, off-DOM-flow, screen-reader-only. */}
      <span
        aria-live="polite"
        className="pointer-events-none absolute h-px w-px overflow-hidden"
        style={{ clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', whiteSpace: 'nowrap' }}
      >
        {announce}
      </span>
    </motion.div>
  );
}

function EmptyState({
  onPick,
  route,
  isEn = false,
}: {
  onPick: (q: string) => void;
  route: string;
  isEn?: boolean;
}) {
  // Wave 52 · concierge digest. Quietly fetches what's pending for the
  // user when the panel opens. Renders nothing while loading; shows
  // "tudo em dia" if zero pendings; otherwise a one-liner. The numbers
  // come from the same query Housy's list_my_pendings tool would run.
  const [digest, setDigest] = useState<Awaited<ReturnType<typeof api.getHousyDigest>> | null>(null);
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const d = await api.getHousyDigest();
        if (alive) setDigest(d);
      } catch {
        // Non-fatal — silently degrade to the generic greeting.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Route-aware prompt suggestions. Each route surfaces queries that
  // make sense *on that page* — the agent picks up context automatically.
  const suggestions: string[] = route.startsWith('/projects/')
    ? isEn
      ? [
          "What's the progress on this project?",
          'How much has been spent so far?',
          'Show me the latest daily-report photo',
        ]
      : [
          'Como tá o progresso dessa obra?',
          'Quanto já foi gasto até agora?',
          'Me mostra a última foto do RDO',
        ]
    : route.startsWith('/vitrine')
      ? isEn
        ? [
            'What is the built area of Gênese?',
            'List the suites with their areas',
            'Color palette for the trellis brise',
          ]
        : [
            'Qual a área construída da Gênese?',
            'Lista as suítes com suas áreas',
            'Palette do brise telado',
          ]
      : isEn
        ? [
            'Do I have anything to do today?',
            'How many projects are active?',
            'How do I approve a registration?',
          ]
        : [
            'Tenho algo pra fazer hoje?',
            'Quantas obras tem rolando?',
            'Como aprovo um cadastro?',
          ];

  // Build the digest line. Comma-separated parts, only show non-zero.
  const digestParts: string[] = (() => {
    if (!digest) return [];
    const out: string[] = [];
    if (digest.unreadMessages > 0) {
      out.push(isEn
        ? `${digest.unreadMessages} unread message${digest.unreadMessages > 1 ? 's' : ''}`
        : `${digest.unreadMessages} mensagem${digest.unreadMessages > 1 ? 's' : ''} não lida${digest.unreadMessages > 1 ? 's' : ''}`);
    }
    if (digest.rncsAssigned > 0) {
      out.push(isEn
        ? `${digest.rncsAssigned} non-conformance${digest.rncsAssigned > 1 ? 's' : ''} for you`
        : `${digest.rncsAssigned} RNC${digest.rncsAssigned > 1 ? 's' : ''} pra você`);
    }
    if (digest.pendingApprovals > 0) {
      out.push(isEn
        ? `${digest.pendingApprovals} approval${digest.pendingApprovals > 1 ? 's' : ''} waiting`
        : `${digest.pendingApprovals} aprovaç${digest.pendingApprovals > 1 ? 'ões' : 'ão'} esperando`);
    }
    if (digest.overdueMedicoes > 0) {
      out.push(isEn
        ? `${digest.overdueMedicoes} overdue measurement${digest.overdueMedicoes > 1 ? 's' : ''}`
        : `${digest.overdueMedicoes} medição${digest.overdueMedicoes > 1 ? 'ões' : ''} atrasada${digest.overdueMedicoes > 1 ? 's' : ''}`);
    }
    return out;
  })();

  return (
    <div className="space-y-4 pt-2">
      <div>
        <p className="text-[15px] font-medium text-ink">
          {isEn ? 'Hi, I am Housy.' : 'Oi, eu sou o Housy.'}
        </p>
        {digest && digestParts.length > 0 && (
          <p className="mt-1 rounded-lg border border-brand-400/30 bg-brand-400/10 px-3 py-2 text-[13px] leading-relaxed text-ink">
            {isEn ? 'You have ' : 'Você tem '}<span className="font-semibold">{digestParts.join(', ')}</span>.
          </p>
        )}
        {digest && digestParts.length === 0 && (
          <p className="mt-1 text-[13px] leading-relaxed text-ok">
            {isEn ? 'All clear — nothing pending for you right now.' : 'Tudo em dia — nada pendente pra você agora.'}
          </p>
        )}
        <p className="mt-1 text-[13px] leading-relaxed text-ink-muted">
          {isEn
            ? 'Ask anything about your projects — status, costs, photos, measurements, or how to do X in the app. I answer with real data when it exists, and let you know when it does not.'
            : 'Pergunta qualquer coisa sobre as obras — status, custos, fotos, medidas, ou como fazer X no app. Respondo com dado real quando existe e aviso quando não.'}
        </p>
      </div>
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
          {isEn ? 'Suggestions' : 'Sugestões'}
        </p>
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            className="pressable block w-full rounded-xl border border-line bg-surface-2 px-3 py-2 text-left text-[13px] text-ink hover:bg-surface-3"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function TurnView({ turn }: { turn: ChatTurn }) {
  const { locale } = useT();
  const isEn = locale === 'en';
  if (turn.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-xl bg-ink px-3 py-2 text-surface">
          {turn.content}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {turn.toolEvents && turn.toolEvents.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {turn.toolEvents.map((e, i) => (
            <span
              key={i}
              className={cn(
                'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                e.kind === 'call'
                  ? 'border-line bg-surface-2 text-ink-muted'
                  : e.detail === 'acesso negado'
                    ? 'border-err/30 bg-err/10 text-err'
                    : e.detail === 'erro'
                      ? 'border-warn/30 bg-warn/10 text-warn'
                      : 'border-ok/30 bg-ok/10 text-ok',
              )}
            >
              {e.kind === 'call' ? labelTool(e.name, 'verb', isEn) : 'ok'} · {labelTool(e.name, 'noun', isEn)}
            </span>
          ))}
        </div>
      )}
      {turn.content && (
        <div className="whitespace-pre-wrap leading-relaxed text-ink">
          {turn.content}
        </div>
      )}
    </div>
  );
}

// Wave 99 · locale-aware tool-name labels for Housy chips. The agent emits raw
// tool names like `query_finance` or `search_photos`; surface them as
// human-readable chips so the user reads the action in their language.
//
// Wave 100 v2 · expanded so EVERY tool the agent might call has a label.
function labelTool(name: string, mode: 'verb' | 'noun', isEn: boolean): string {
  type Entry = { verb: string; noun: string; verbEn: string; nounEn: string };
  const map: Record<string, Entry> = {
    // Project surfaces
    list_projects:           { verb: 'Listando obras',             noun: 'obras',          verbEn: 'Listing projects',      nounEn: 'projects' },
    get_project_status:      { verb: 'Olhando a obra',             noun: 'status da obra', verbEn: 'Checking project',      nounEn: 'project status' },
    get_project_brief:       { verb: 'Lendo o briefing',           noun: 'briefing',       verbEn: 'Reading brief',         nounEn: 'brief' },
    get_project_team:        { verb: 'Olhando o time da obra',     noun: 'time',           verbEn: 'Checking team',         nounEn: 'team' },
    get_recent_activity:     { verb: 'Lendo atividade recente',    noun: 'atividade',      verbEn: 'Reading recent activity', nounEn: 'activity' },
    update_project_status:   { verb: 'Atualizando status da obra', noun: 'status da obra', verbEn: 'Updating project status', nounEn: 'project status' },
    delete_project:          { verb: 'Excluindo obra',             noun: 'obra',           verbEn: 'Deleting project',      nounEn: 'project' },
    // Money
    query_finance:           { verb: 'Consultando financeiro',     noun: 'financeiro',     verbEn: 'Checking financials',   nounEn: 'financials' },
    query_finance_portfolio:  { verb: 'Olhando carteira',          noun: 'carteira',       verbEn: 'Checking portfolio',    nounEn: 'portfolio' },
    // Day-to-day
    last_rdo:                { verb: 'Olhando o último RDO',       noun: 'RDO',            verbEn: 'Reading last daily report', nounEn: 'daily report' },
    query_issues:            { verb: 'Buscando RNCs',              noun: 'RNCs',           verbEn: 'Searching non-conformances', nounEn: 'non-conformances' },
    query_medicoes:          { verb: 'Conferindo medições',        noun: 'medições',       verbEn: 'Checking measurements', nounEn: 'measurements' },
    list_anomalies:          { verb: 'Buscando anomalias',         noun: 'anomalias',      verbEn: 'Checking unusual activity', nounEn: 'unusual activity' },
    list_my_pendings:        { verb: 'Buscando suas pendências',   noun: 'pendências',     verbEn: 'Fetching your pending items', nounEn: 'pending items' },
    // Photos / spec
    search_photos:           { verb: 'Buscando fotos',             noun: 'fotos',          verbEn: 'Searching photos',      nounEn: 'photos' },
    lookup_spec:             { verb: 'Lendo a ficha técnica',      noun: 'ficha técnica',  verbEn: 'Reading spec sheet',    nounEn: 'spec sheet' },
    extract_spec_from_text:  { verb: 'Extraindo ficha técnica',    noun: 'extração',       verbEn: 'Extracting spec',       nounEn: 'extraction' },
    // Suppliers
    lookup_supplier:         { verb: 'Buscando fornecedor',        noun: 'fornecedor',     verbEn: 'Looking up supplier',   nounEn: 'supplier' },
    // Tasks
    create_task:             { verb: 'Criando tarefa',             noun: 'tarefa',         verbEn: 'Creating task',         nounEn: 'task' },
    // Help / docs
    help_lookup:             { verb: 'Olhando o manual',           noun: 'manual',         verbEn: 'Checking the docs',     nounEn: 'docs' },
    // Legacy / not yet wired but kept for safety
    list_stages:             { verb: 'Listando etapas',            noun: 'etapas',         verbEn: 'Listing stages',        nounEn: 'stages' },
  };
  const entry = map[name];
  if (!entry) {
    // Unmapped tool — convert snake_case to a readable phrase
    // instead of leaking the identifier as-is.
    const pretty = name.replace(/_/g, ' ');
    return mode === 'verb'
      ? (isEn ? `Checking ${pretty}…` : `Consultando ${pretty}…`)
      : pretty;
  }
  if (isEn) {
    return mode === 'verb' ? `${entry.verbEn}…` : entry.nounEn;
  }
  return mode === 'verb' ? `${entry.verb}…` : entry.noun;
}

// ─── Wave 314L · Feedback panel ─────────────────────────────────────────
//
// Painel inline (não modal) que aparece dentro do próprio Housy quando
// o usuário clica "Feedback". Vai pra fila de curadoria (JP+Claude); só
// vira backlog depois que aprovamos.
function FeedbackPanel({
  routeContext,
  onClose,
}: {
  routeContext: string;
  onClose: () => void;
}) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const [kind, setKind] = useState<'elogio' | 'problema' | 'sugestao'>('sugestao');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const kindLabels: Record<'elogio' | 'problema' | 'sugestao', string> = isEn
    ? { elogio: 'praise', problema: 'problem', sugestao: 'suggestion' }
    : { elogio: 'elogio', problema: 'problema', sugestao: 'sugestão' };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.submitFeedback({ kind, text: text.trim(), routeContext });
      setSent(true);
      setTimeout(() => onClose(), 1400);
    } catch (err) {
      setError(err instanceof Error ? err.message : (isEn ? 'Failed to send.' : 'Falha ao enviar.'));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="border-b border-line bg-ok/5 px-4 py-3 text-[12px] text-ok">
        {isEn ? 'Feedback sent · going to curation.' : 'Feedback enviado · vai pra curadoria.'}
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-2 border-b border-line bg-surface-2/40 px-4 py-3"
    >
      <p className="text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
        {isEn ? 'Feedback · goes to curation' : 'Feedback · vai pra curadoria'}
      </p>
      <div className="flex gap-1.5">
        {(['elogio', 'problema', 'sugestao'] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKind(k)}
            className={`pressable rounded-full border px-2.5 py-0.5 text-[11px] capitalize transition-colors ${
              kind === k
                ? 'border-ink bg-ink text-surface'
                : 'border-line bg-surface text-ink-muted hover:text-ink'
            }`}
          >
            {kindLabels[k]}
          </button>
        ))}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
        rows={3}
        maxLength={4000}
        placeholder={isEn ? 'What is working, what is not, what could be better...' : 'O que tá bom, o que tá ruim, o que poderia ser melhor...'}
        className="block w-full resize-none rounded border border-line bg-surface px-2 py-1.5 text-[12px] text-ink placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none"
        autoFocus
      />
      {error && <p className="text-[11px] text-error">{error}</p>}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] text-ink-subtle">
          {routeContext ? `${isEn ? 'Page:' : 'Página:'} ${routeContext}` : ''}
        </span>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={onClose}
            className="pressable rounded border border-line bg-surface px-2.5 py-1 text-[11px] text-ink-muted hover:text-ink"
          >
            {isEn ? 'Cancel' : 'Cancelar'}
          </button>
          <button
            type="submit"
            disabled={busy || !text.trim()}
            className="pressable rounded bg-brand-500 px-2.5 py-1 text-[11px] font-semibold text-white disabled:opacity-50"
          >
            {busy ? (isEn ? 'Sending…' : 'Enviando…') : (isEn ? 'Send' : 'Enviar')}
          </button>
        </div>
      </div>
    </form>
  );
}
